import { Capacitor, registerPlugin } from "@capacitor/core";
import {
  fetchReaderVersionManifest,
  type ReaderVersionManifest,
} from "@nevisign/reader-core";
import {
  APP_VERSION_CODE,
  APP_VERSION_NAME,
  DEFAULT_VERSION_JSON_URL,
  VERSION_JSON_URLS,
} from "./constants";
import { appendLog } from "./logger";

type ApkInstallerPlugin = {
  canRequestPackageInstalls(): Promise<{ allowed: boolean }>;
  openInstallPermissionSettings(): Promise<void>;
  installApk(options: { path: string }): Promise<{ ok: boolean }>;
  downloadAndInstall(options: { url: string }): Promise<{ ok: boolean; path?: string }>;
  addListener(
    eventName: "downloadProgress",
    listenerFunc: (event: { progress: number }) => void
  ): Promise<{ remove: () => Promise<void> }>;
};

const ApkInstaller = registerPlugin<ApkInstallerPlugin>("ApkInstaller");

export type AppVersionInfo = {
  versionCode: number;
  versionName: string;
};

export function getBundledVersion(): AppVersionInfo {
  return {
    versionCode: APP_VERSION_CODE,
    versionName: APP_VERSION_NAME,
  };
}

export async function getNativeVersion(): Promise<AppVersionInfo> {
  if (!Capacitor.isNativePlatform()) return getBundledVersion();
  if (!Capacitor.isPluginAvailable("App")) return getBundledVersion();
  try {
    const { App } = await import("@capacitor/app");
    const info = await App.getInfo();
    return {
      versionCode: Number(info.build) || APP_VERSION_CODE,
      versionName: info.version || APP_VERSION_NAME,
    };
  } catch {
    return getBundledVersion();
  }
}

export type UpdateCheckResult = {
  manifest: ReaderVersionManifest;
  current: AppVersionInfo;
  updateAvailable: boolean;
  forceUpdate: boolean;
  sourceUrl: string;
};

export async function checkForUpdate(
  versionUrl?: string
): Promise<UpdateCheckResult> {
  const current = await getNativeVersion();
  const urls = versionUrl ? [versionUrl] : [...VERSION_JSON_URLS];

  let lastError: unknown;
  for (const url of urls) {
    try {
      // Cache-bust so WCDN/SMART cannot return a pinned stale manifest body.
      const bust = `${url}${url.includes("?") ? "&" : "?"}cb=${Date.now()}`;
      appendLog("info", "update", `checking ${bust}`, current);
      const manifest = await fetchReaderVersionManifest(bust);
      const updateAvailable = manifest.versionCode > current.versionCode;
      const forceUpdate = Boolean(manifest.forceUpdate) && updateAvailable;
      appendLog(
        "info",
        "update",
        updateAvailable
          ? `update available ${manifest.versionName} (${manifest.versionCode})`
          : "up to date",
        { manifest, sourceUrl: url }
      );
      return {
        manifest,
        current,
        updateAvailable,
        forceUpdate,
        sourceUrl: url,
      };
    } catch (err) {
      lastError = err;
      appendLog("warn", "update", `manifest miss ${url}`, err);
    }
  }
  throw lastError instanceof Error
    ? lastError
    : new Error(`بررسی به‌روزرسانی ناموفق (${DEFAULT_VERSION_JSON_URL})`);
}

function pluginErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === "object") {
    const o = err as { message?: string; code?: string; errorMessage?: string };
    if (o.code === "UNIMPLEMENTED") {
      return "نصب به‌روزرسانی در این نسخه پشتیبانی نمی‌شود. APK را دستی نصب کنید.";
    }
    const msg = o.message || o.errorMessage;
    if (typeof msg === "string" && msg.trim()) return msg;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/**
 * Download APK natively (stream to cache) and open the system installer
 * via FileProvider. Never ship APK bytes through the JS↔native bridge —
 * that crashes Android with TransactionTooLargeException on multi‑MB files.
 */
export async function downloadAndInstallApk(
  apkUrl: string,
  onProgress?: (pct: number) => void
): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    throw new Error("نصب مستقیم فقط روی اندروید پشتیبانی می‌شود.");
  }
  if (!Capacitor.isPluginAvailable("ApkInstaller")) {
    throw new Error(
      "نصب به‌روزرسانی در این نسخه پشتیبانی نمی‌شود. APK را دستی نصب کنید."
    );
  }

  appendLog("info", "update", `native download+install ${apkUrl}`);
  onProgress?.(2);

  let removeProgress: (() => Promise<void>) | undefined;
  try {
    const handle = await ApkInstaller.addListener("downloadProgress", (ev) => {
      if (typeof ev?.progress === "number") onProgress?.(ev.progress);
    });
    removeProgress = () => handle.remove();
  } catch (err) {
    appendLog("warn", "update", "progress listener unavailable", err);
  }

  try {
    await ApkInstaller.downloadAndInstall({ url: apkUrl });
    onProgress?.(100);
  } catch (err) {
    throw new Error(
      pluginErrorMessage(err, "نصب به‌روزرسانی ناموفق بود")
    );
  } finally {
    try {
      await removeProgress?.();
    } catch {
      /* ignore */
    }
  }
}
